/**
 * Easing utilities for smooth motion curves
 */
class EasingUtils {
    static cosineEase(ratio) {
        return (1 - Math.cos(ratio * Math.PI)) / 2;
    }
    
    static calculateEasedRatio(distance, maxDistance) {
        if (distance >= maxDistance) return 1;
        return this.cosineEase(distance / maxDistance);
    }
}

/**
 * Animation timing state machine
 */
class AnimationState {
    constructor(config) {
        this.config = config;
        this.reset();
    }
    
    reset() {
        this.phase = 'stopped'; // stopped, startup, accelerating, running, paused
        this.startupUntil = 0;
        this.pauseUntil = 0;
        this.accelerationStartTime = 0;
        this.isManuallyPaused = false;
    }
    
    start() {
        this.phase = 'startup';
        const now = performance.now();
        this.startupUntil = now + this.config.startupDelay;
        this.accelerationStartTime = this.startupUntil;
    }
    
    pause(duration, isManual = true) {
        this.phase = 'paused';
        this.isManuallyPaused = isManual;
        if (duration > 0) {
            this.pauseUntil = performance.now() + duration;
        }
    }
    
    resume() {
        this.phase = 'running';
        this.isManuallyPaused = false;
        this.pauseUntil = 0;
    }
    
    stop() {
        this.reset();
    }
    
    handleEdgeHit() {
        if (this.config.pauseAtEdges > 0) {
            const now = performance.now();
            this.pause(this.config.pauseAtEdges, false);
            this.startupUntil = now + this.config.pauseAtEdges + this.config.startupDelay;
            this.accelerationStartTime = this.startupUntil;
        } else {
            this.phase = 'startup';
            const now = performance.now();
            this.startupUntil = now + this.config.startupDelay;
            this.accelerationStartTime = this.startupUntil;
        }
    }
    
    update(currentTime) {
        switch (this.phase) {
            case 'startup':
                if (currentTime >= this.startupUntil) {
                    this.phase = 'accelerating';
                }
                break;
                
            case 'accelerating':
                const accelerationDuration = this.config.edgeEaseDistance / this.config.speed * 16.67;
                if (currentTime >= this.accelerationStartTime + accelerationDuration) {
                    this.phase = 'running';
                }
                break;
                
            case 'paused':
                if (!this.isManuallyPaused && currentTime >= this.pauseUntil) {
                    this.phase = 'startup';
                }
                break;
        }
    }
    
    shouldMove(currentTime) {
        this.update(currentTime);
        return this.phase === 'accelerating' || this.phase === 'running';
    }
    
    getSpeedMultiplier(currentTime) {
        if (this.phase === 'accelerating') {
            const accelerationDuration = this.config.edgeEaseDistance / this.config.speed * 16.67;
            const elapsed = currentTime - this.accelerationStartTime;
            return EasingUtils.calculateEasedRatio(elapsed, accelerationDuration);
        }
        return this.phase === 'running' ? 1 : 0;
    }
}

/**
 * Handles movement calculations for a single axis
 */
class AxisMovement {
    constructor(axis, config) {
        this.axis = axis; // 'x' or 'y'
        this.config = config;
        this.direction = 1;
        this.canMove = false;
        this.minBound = 0;
        this.maxBound = 0;
    }
    
    updateBounds(wrapperSize, containerSize, wrapperPosition) {
        this.canMove = wrapperSize > containerSize;
        if (this.canMove) {
            this.maxBound = 0;
            this.minBound = -(wrapperSize - containerSize);
        }
    }
    
    calculateMovement(position, baseSpeed, deltaTime) {
        if (!this.canMove) return { newPosition: position, hitEdge: false };
        
        // Calculate edge easing
        const distanceFromEdge = this.direction > 0 
            ? this.maxBound - position 
            : position - this.minBound;
            
        const easedSpeed = this.calculateEasedSpeed(distanceFromEdge, baseSpeed);
        const movement = easedSpeed * (deltaTime / 16.67);
        let newPosition = position + (this.direction * movement);
        let hitEdge = false;
        
        // Check boundaries
        if (newPosition <= this.minBound) {
            newPosition = this.minBound;
            this.direction = 1;
            hitEdge = true;
        } else if (newPosition >= this.maxBound) {
            newPosition = this.maxBound;
            this.direction = -1;
            hitEdge = true;
        }
        
        return { newPosition, hitEdge };
    }
    
    calculateEasedSpeed(distanceFromEdge, baseSpeed) {
        if (distanceFromEdge <= this.config.edgeEaseDistance) {
            const easeRatio = EasingUtils.calculateEasedRatio(distanceFromEdge, this.config.edgeEaseDistance);
            return this.config.minSpeed + (baseSpeed - this.config.minSpeed) * easeRatio;
        }
        return baseSpeed;
    }
}

/**
 * AutoPan - Refactored with improved architecture
 */
export class AutoPan {
    constructor(imageTransform, config = {}) {
        this.imageTransform = imageTransform;
        this.config = {
            speed: 0.5,
            pauseAtEdges: 2000,
            edgeEaseDistance: 50,
            minSpeed: 0.1,
            startupDelay: 2000,
            enabled: true,
            ...config
        };
        
        // Composition over complex state management
        this.animationState = new AnimationState(this.config);
        this.xMovement = new AxisMovement('x', this.config);
        this.yMovement = new AxisMovement('y', this.config);
        
        // Simple animation tracking
        this.animationId = null;
        this.lastFrameTime = 0;
        
        // Event system
        this.listeners = new Map();
        
        this.setupTransformListener();
    }
    
    // Event system (unchanged)
    addEventListener(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    
    emit(event, data = {}) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(callback => callback(data));
        }
    }
    
    setupTransformListener() {
        this.imageTransform.addEventListener('transformApplied', () => {
            this.updateBounds();
        });
        
        this.imageTransform.addEventListener('baseTransformCalculated', () => {
            this.updateBounds();
        });
    }
    
    updateBounds() {
        const state = this.imageTransform.getState();
        const slideRect = this.imageTransform.slideElement.getBoundingClientRect();
        
        // Only auto-pan for cover images
        if (this.imageTransform.imageFit !== 'cover') {
            this.xMovement.canMove = false;
            this.yMovement.canMove = false;
            this.stop();
            return;
        }
        
        // Check if auto-panning is possible (regardless of zoom state)
        const canAutoPan = this.imageTransform.canAutoPan();
        if (!canAutoPan) {
            this.xMovement.canMove = false;
            this.yMovement.canMove = false;
            this.stop();
            return;
        }
        
        this.xMovement.updateBounds(state.wrapperWidth, slideRect.width, state.wrapperLeft);
        this.yMovement.updateBounds(state.wrapperHeight, slideRect.height, state.wrapperTop);
        
        this.emit('boundsUpdated', {
            canPanX: this.xMovement.canMove,
            canPanY: this.yMovement.canMove
        });
    }
    
    start() {
        if (!this.config.enabled || this.isRunning()) return;
        if (!this.xMovement.canMove && !this.yMovement.canMove) return;
        
        this.animationState.start();
        this.lastFrameTime = performance.now();
        this.emit('started');
        this.animate();
    }
    
    stop() {
        if (!this.isRunning()) return;
        
        this.animationState.stop();
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        this.emit('stopped');
    }
    
    pause(duration = 0) {
        if (!this.isRunning()) return;
        this.animationState.pause(duration, true);
        this.emit('paused', { duration });
    }
    
    resume() {
        if (!this.isRunning()) return;
        this.animationState.resume();
        this.emit('resumed');
    }
    
    animate() {
        if (!this.isRunning()) return;
        
        const currentTime = performance.now();
        const deltaTime = currentTime - this.lastFrameTime;
        this.lastFrameTime = currentTime;
        
        if (!this.animationState.shouldMove(currentTime)) {
            this.animationId = requestAnimationFrame(() => this.animate());
            return;
        }
        
        const state = this.imageTransform.getState();
        const speedMultiplier = this.animationState.getSpeedMultiplier(currentTime);
        const effectiveSpeed = this.config.speed * speedMultiplier;
        
        // Calculate movement for both axes
        const xResult = this.xMovement.calculateMovement(state.wrapperLeft, effectiveSpeed, deltaTime);
        const yResult = this.yMovement.calculateMovement(state.wrapperTop, effectiveSpeed, deltaTime);
        
        // Apply movement
        this.imageTransform.wrapperLeft = xResult.newPosition;
        this.imageTransform.wrapperTop = yResult.newPosition;
        this.imageTransform.applyTransform(true);
        
        // Handle edge hits
        if (xResult.hitEdge || yResult.hitEdge) {
            this.animationState.handleEdgeHit();
            this.emit('edgeReached', {
                left: xResult.newPosition,
                top: yResult.newPosition,
                directionX: this.xMovement.direction,
                directionY: this.yMovement.direction
            });
        }
        
        this.animationId = requestAnimationFrame(() => this.animate());
    }
    
    // Helper methods
    isRunning() {
        return this.animationState.phase !== 'stopped';
    }
    
    getState() {
        return {
            isRunning: this.isRunning(),
            phase: this.animationState.phase,
            canPanX: this.xMovement.canMove,
            canPanY: this.yMovement.canMove,
            directionX: this.xMovement.direction,
            directionY: this.yMovement.direction,
            speed: this.config.speed,
            enabled: this.config.enabled
        };
    }
    
    // Configuration setters
    setSpeed(speed) {
        this.config.speed = Math.max(0, speed);
        this.emit('speedChanged', { speed: this.config.speed });
    }
    
    setPauseAtEdges(duration) {
        this.config.pauseAtEdges = Math.max(0, duration);
        this.emit('pauseAtEdgesChanged', { duration: this.config.pauseAtEdges });
    }
    
    setEnabled(enabled) {
        this.config.enabled = enabled;
        if (!enabled && this.isRunning()) {
            this.stop();
        }
        this.emit('enabledChanged', { enabled });
    }
    
    destroy() {
        this.stop();
        this.listeners.clear();
        this.imageTransform = null;
    }
}